import { bcLong } from '@lib2/model/util/string/BaseConverter';
import {
  APP_STATE_TYPE_ID,
  APP_WEB_DOMAIN_LABELS,
  SPOTLIGHT_OPTIONS,
} from '@/lib/constants/app';
import type {
  AppCfgsDto,
  AppCfgFilesDto,
  AppCfgCreateReq,
  AppCfgUpdateReq,
  AppCfgWebRow,
  AppCfgWebUpdateReq,
  AppGeneralFormData,
  AppWebFormData,
  AppCardData,
} from '@/lib/types/App';

export class AppUtils {
  // ── ID Conversions ──

  static getDisplayId(appCfgId: number): string {
    return bcLong.getStr(appCfgId);
  }

  static getNumericId(displayId: string): number {
    return bcLong.getNum(displayId);
  }

  // ── App DTO → Form data ──

  static transformAppToFormData(app: AppCfgsDto): AppGeneralFormData {
    const { core, orgInfo, social, extplat } = app.appCfg;
    return {
      name: orgInfo.name,
      desc: orgInfo.desc ?? '',
      websiteDomainPath: orgInfo.websiteDomainPath ?? '',
      rootLlId: core.rootLlId,
      defaultPlayerId: core.defaultPlayerId,
      isChildTargeted: core.isChildTargeted,
      spotlightCatLLId: core.spotlightCatLLId ?? SPOTLIGHT_OPTIONS.RANDOM_ANY,
      fbId: social.fbId ?? '',
      igId: social.igId ?? '',
      xId: social.xId ?? '',
      ytId: social.ytId ?? '',
      gaId: extplat.gaId ?? '',
    };
  }

  // ── Form data → Update request (includes updated timestamp) ──

  static transformFormToUpdateReq(
    form: AppGeneralFormData,
    currentApp: AppCfgsDto,
  ): AppCfgUpdateReq {
    return {
      core: {
        appStateTypeId: currentApp.appCfg.core.appStateTypeId,
        rootLLId: form.rootLlId, // NOTE: uppercase LL in update
        defaultPlayerId: form.defaultPlayerId,
        isChildTargeted: form.isChildTargeted,
        spotlightCatLLId:
          form.spotlightCatLLId === SPOTLIGHT_OPTIONS.RANDOM_ANY
            ? null
            : form.spotlightCatLLId,
        updated: currentApp.appCfg.core.updated, // CRITICAL: pass current timestamp
      },
      orgInfo: {
        name: form.name,
        desc: form.desc || null,
        websiteDomainPath: form.websiteDomainPath || null,
      },
      social: {
        fbId: form.fbId || null,
        igId: form.igId || null,
        xId: form.xId || null,
        ytId: form.ytId || null,
      },
      extplat: {
        gaId: form.gaId || null,
      },
    };
  }

  // ── Web DTO → Form data ──

  static transformWebToFormData(web: AppCfgWebRow): AppWebFormData {
    return {
      customDomain: web.core.customDomain ?? '',
      styleIsDarkMode: web.style.styleIsDarkMode,
      styleBGColorHex: web.style.styleBGColorHex ?? '',
      styleTextColorHex: web.style.styleTextColorHex ?? '',
      allowIndex: web.seo.allowIndex,
      appStateTypeId: web.core.appStateTypeId,
    };
  }

  // ── Web form → Update request ──

  static transformWebFormToUpdateReq(
    form: AppWebFormData,
    currentWeb: AppCfgWebRow,
  ): AppCfgWebUpdateReq {
    return {
      core: {
        appStateTypeId: form.appStateTypeId,
        customName: currentWeb.core.customName, // Read-only after creation
        sharedDomainId: currentWeb.core.sharedDomainId,
        customDomain: form.customDomain || null,
        updated: currentWeb.core.updated, // CRITICAL
      },
      style: {
        styleIsDarkMode: form.styleIsDarkMode,
        styleBGColorHex: form.styleBGColorHex || null,
        styleTextColorHex: form.styleTextColorHex || null,
      },
      seo: {
        allowIndex: form.allowIndex,
      },
    };
  }

  // ── Create request builder ──

  static buildCreateReq(
    name: string,
    rootLlId: number,
    defaultPlayerId: number,
  ): AppCfgCreateReq {
    return {
      core: {
        appStateTypeId: APP_STATE_TYPE_ID.Active,
        rootLlId, // lowercase 'l' in create
        defaultPlayerId,
      },
      orgInfo: {
        name,
        desc: null,
        websiteDomainPath: null,
      },
      social: {
        fbId: null,
        igId: null,
        xId: null,
        ytId: null,
      },
      extplat: {
        gaId: null,
      },
    };
  }

  // ── Toggle state builder ──

  static buildToggleUpdateReq(currentApp: AppCfgsDto): AppCfgUpdateReq {
    const isActive = currentApp.appCfg.core.appStateTypeId === APP_STATE_TYPE_ID.Active;
    const newState = isActive ? APP_STATE_TYPE_ID.Inactive : APP_STATE_TYPE_ID.Active;
    return {
      core: {
        appStateTypeId: newState,
        rootLLId: currentApp.appCfg.core.rootLlId,
        defaultPlayerId: currentApp.appCfg.core.defaultPlayerId,
        isChildTargeted: currentApp.appCfg.core.isChildTargeted,
        spotlightCatLLId: currentApp.appCfg.core.spotlightCatLLId,
        updated: currentApp.appCfg.core.updated,
      },
      orgInfo: currentApp.appCfg.orgInfo,
      social: currentApp.appCfg.social,
      extplat: currentApp.appCfg.extplat,
    };
  }

  // ── Card display data builder ──

  static buildCardData(appFiles: AppCfgFilesDto, contentGroupTitle: string): AppCardData {
    const app = appFiles.appCfgDto;
    return {
      appCfgId: app.appCfg.core.appCfgId,
      displayId: AppUtils.getDisplayId(app.appCfg.core.appCfgId),
      name: app.appCfg.orgInfo.name,
      isActive: app.appCfg.core.appStateTypeId === APP_STATE_TYPE_ID.Active,
      contentGroupTitle,
      hasWeb: app.web != null,
      hasRoku: app.roku != null,
      webDomain: app.web
        ? `${app.web.core.customName}.${APP_WEB_DOMAIN_LABELS[app.web.core.sharedDomainId]}`
        : null,
      created: app.appCfg.core.created,
    };
  }
}
